import { useId } from 'react';
import type { CSSProperties } from 'react';
import { PLATE, SEGMENT_OPACITIES, SHAPE } from './shape';
import './Loader.css';

export interface LoaderProps {
  /**
   * Extra classes. This is where the colour comes from, e.g. `text-white`.
   * A screen that needs a different size overrides the var here too:
   * `[--size:52px]`.
   */
  className?: string;
}

/**
 * Brand loader: a glowing spot travels around the outline of the Repackz mark.
 *
 * Decorative on purpose (`aria-hidden`) — the consumer owns the announcement,
 * either as visible text next to it or `role="status"` on its own wrapper.
 *
 * Colour is `currentColor`, so paint it with a theme token on the loader itself
 * or on any ancestor: `<Loader className="text-white" />`.
 */
export function Loader({ className = '' }: LoaderProps) {
  // Filter ids have to be unique per instance — two loaders sharing one id
  // collapse into a single filter. useId() is already fragment-safe in React 19.
  const uid = useId();
  const bloomId = `ds-loader-bloom-${uid}`;
  const glowId = `ds-loader-glow-${uid}`;

  const segments = SEGMENT_OPACITIES.map((opacity, index) => (
    <path
      key={opacity}
      className="ds-loader-seg"
      // safe: CSSProperties has no index signature for custom --* keys
      style={{ opacity, '--i': index } as CSSProperties}
      pathLength="100"
      d={SHAPE}
    />
  ));

  return (
    <svg
      aria-hidden
      className={`ds-loader ${className}`.trim()}
      viewBox="0 0 52 52"
      fill="none"
    >
      <filter id={bloomId} x="-45%" y="-45%" width="190%" height="190%">
        <feGaussianBlur stdDeviation="1.7" />
      </filter>
      <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="0.4" />
      </filter>

      <path className="ds-loader-plate" d={PLATE} />
      <path className="ds-loader-track" pathLength="100" d={SHAPE} />

      {/* The same stack twice: a wide blur for the bloom, a narrow one for the
          soft edges of the spot itself. */}
      <g filter={`url(#${bloomId})`} opacity="0.6">
        {segments}
      </g>
      <g filter={`url(#${glowId})`}>{segments}</g>
    </svg>
  );
}
